import { useState } from 'react';
import { Users, Loader2, Plus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserCard } from '../components/UserCard';
import { UsersFilters } from '../components/UsersFilters';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '../hooks/useUsers';
import { useCompanies } from '../../companies/hooks/useCompanies';
import type { User, UserFilters, UserRole, UserStatus, CreateUserDto, UpdateUserDto } from '../types/user.types';
import { USER_ROLE_CONFIG, USER_STATUS_CONFIG } from '../types/user.types';

export function UsersPage() {
  const [filters, setFilters] = useState<UserFilters>({});
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editData, setEditData] = useState<UpdateUserDto>({});
  const [newUser, setNewUser] = useState<CreateUserDto>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'recruiter',
  });
  const [showPassword, setShowPassword] = useState(false);

  const { data, isLoading, error } = useUsers(filters, { page, limit: 20 });
  const { data: companiesData } = useCompanies({}, { limit: 100 }, 'tenants');
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const resetNewUser = () => {
    setNewUser({
      email: '',
      firstName: '',
      lastName: '',
      role: 'recruiter',
    });
  };

  const handleAddUser = () => {
    if (!newUser.email.trim() || !newUser.firstName.trim() || !newUser.lastName.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!newUser.companyId) {
      toast.error('Veuillez sélectionner une société');
      return;
    }

    createUser.mutate(newUser, {
      onSuccess: () => {
        const msg = newUser.temporaryPassword
          ? 'Utilisateur créé avec un mot de passe temporaire'
          : 'Utilisateur créé — un email de création de mot de passe a été envoyé';
        toast.success(msg);
        setShowAddDialog(false);
        resetNewUser();
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Erreur lors de la création');
      },
    });
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || undefined,
      role: user.role,
      status: user.status,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!selectedUser) return;

    updateUser.mutate(
      { id: selectedUser.id, data: editData },
      {
        onSuccess: () => {
          toast.success('Utilisateur mis à jour');
          setShowEditDialog(false);
          setSelectedUser(null);
        },
        onError: () => {
          toast.error('Erreur lors de la mise à jour');
        },
      }
    );
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!selectedUser) return;

    deleteUser.mutate(selectedUser.id, {
      onSuccess: () => {
        toast.success('Utilisateur supprimé');
        setShowDeleteDialog(false);
        setSelectedUser(null);
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-copper" />
            Utilisateurs
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les utilisateurs de votre tenant
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un utilisateur
        </Button>
      </div>

      {/* Filtres */}
      <UsersFilters filters={filters} onFiltersChange={setFilters} />

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-copper" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-destructive">
          Erreur lors du chargement des utilisateurs
        </div>
      ) : !data?.data.length ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
        </div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">
            {data.meta.total} utilisateur{data.meta.total > 1 ? 's' : ''}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.data.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
              />
            ))}
          </div>

          {/* Pagination */}
          {data.meta.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédent
              </Button>
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                Page {page} sur {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === data.meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          )}
        </>
      )}

      {/* Dialog Ajouter */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) { resetNewUser(); setShowPassword(false); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-firstName">Prénom *</Label>
                <Input
                  id="new-firstName"
                  value={newUser.firstName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, firstName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-lastName">Nom *</Label>
                <Input
                  id="new-lastName"
                  value={newUser.lastName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, lastName: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">Email *</Label>
              <Input
                id="new-email"
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-company">Société *</Label>
              <Select
                value={newUser.companyId ? String(newUser.companyId) : ''}
                onValueChange={(value) =>
                  setNewUser({ ...newUser, companyId: Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une société" />
                </SelectTrigger>
                <SelectContent>
                  {companiesData?.data.map((company) => (
                    <SelectItem key={company.id} value={String(company.id)}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-role">Rôle *</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) =>
                    setNewUser({ ...newUser, role: value as UserRole })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(USER_ROLE_CONFIG) as UserRole[]).map((role) => (
                      <SelectItem key={role} value={role}>
                        {USER_ROLE_CONFIG[role].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-phone">Téléphone</Label>
                <Input
                  id="new-phone"
                  value={newUser.phone || ''}
                  onChange={(e) =>
                    setNewUser({ ...newUser, phone: e.target.value || undefined })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Mot de passe temporaire</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.temporaryPassword || ''}
                  onChange={(e) =>
                    setNewUser({ ...newUser, temporaryPassword: e.target.value || undefined })
                  }
                  placeholder="Min. 8 caractères"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                L'utilisateur devra le changer à sa première connexion.
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
            {newUser.temporaryPassword
              ? 'L\'utilisateur se connectera avec ce mot de passe temporaire et devra le changer.'
              : 'Un email sera envoyé à l\'utilisateur avec un lien pour créer son mot de passe.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddUser} disabled={createUser.isPending}>
              {createUser.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Créer l'utilisateur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifier */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">Prénom</Label>
                <Input
                  id="edit-firstName"
                  value={editData.firstName || ''}
                  onChange={(e) =>
                    setEditData({ ...editData, firstName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Nom</Label>
                <Input
                  id="edit-lastName"
                  value={editData.lastName || ''}
                  onChange={(e) =>
                    setEditData({ ...editData, lastName: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email || ''}
                onChange={(e) =>
                  setEditData({ ...editData, email: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Téléphone</Label>
              <Input
                id="edit-phone"
                value={editData.phone || ''}
                onChange={(e) =>
                  setEditData({ ...editData, phone: e.target.value || undefined })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-role">Rôle</Label>
                <Select
                  value={editData.role || 'recruiter'}
                  onValueChange={(value) =>
                    setEditData({ ...editData, role: value as UserRole })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(USER_ROLE_CONFIG) as UserRole[]).map((role) => (
                      <SelectItem key={role} value={role}>
                        {USER_ROLE_CONFIG[role].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Statut</Label>
                <Select
                  value={editData.status || 'active'}
                  onValueChange={(value) =>
                    setEditData({ ...editData, status: value as UserStatus })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(USER_STATUS_CONFIG) as UserStatus[]).map((status) => (
                      <SelectItem key={status} value={status}>
                        {USER_STATUS_CONFIG[status].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateUser.isPending}>
              {updateUser.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Supprimer */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{selectedUser?.fullName}" ?
              Cette action désactivera son accès à l'application.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUser.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
